import React from "react";
import type { Entity, Relationship, Event } from "@nexus/contracts";
import { REL_TYPE_LABELS } from "../types";
import { X, Building2, Globe2, Link2, Calendar, Award } from "lucide-react";

interface EntityDetailModalProps {
  entity: Entity | null;
  relationships: Relationship[];
  events: Event[];
  entities: Entity[];
  onClose: () => void;
  onOpenExplain: (entityId: string) => void;
  onSelectEvent: (event: Event) => void;
}

export const EntityDetailModal: React.FC<EntityDetailModalProps> = ({
  entity,
  relationships,
  events,
  entities,
  onClose,
  onOpenExplain,
  onSelectEvent,
}) => {
  if (!entity) return null;

  const entityMap = new Map<string, Entity>();
  for (const e of entities) {
    entityMap.set(e.id, e);
  }

  // Outgoing and Incoming Relationships
  const outgoing = relationships.filter((r) => r.fromId === entity.id);
  const incoming = relationships.filter((r) => r.toId === entity.id);

  // Associated events
  const relatedEvents = events.filter(
    (ev) => ev.actorId === entity.id || ev.targetIds.includes(entity.id),
  );

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-container" style={{ maxWidth: "800px" }} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "6px",
                background: "rgba(6, 182, 212, 0.2)",
                border: "1px solid var(--accent-cyan)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {entity.type === "company" ? (
                <Building2 size={18} color="var(--accent-cyan)" />
              ) : (
                <Globe2 size={18} color="var(--accent-cyan)" />
              )}
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <h2 style={{ fontSize: "16px", fontWeight: 700, color: "#ffffff" }}>
                  {entity.nameKo} ({entity.nameEn})
                </h2>
                <span className="badge-period" style={{ textTransform: "uppercase" }}>
                  {entity.type}
                </span>
                {entity.indexEligible && (
                  <span className="badge-period" style={{ color: "var(--accent-cyan)", borderColor: "var(--accent-cyan)" }}>
                    ★ 지수 산출 대상국
                  </span>
                )}
              </div>
              <div className="font-mono" style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "2px" }}>
                ID: {entity.id} · 좌표: [{entity.lat.toFixed(2)}°, {entity.lon.toFixed(2)}°]
                {entity.iso2 && ` · ISO2: ${entity.iso2}`}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {entity.indexEligible && (
              <button
                className="btn-secondary"
                onClick={() => onOpenExplain(entity.id)}
                style={{ display: "flex", alignItems: "center", gap: "4px", color: "var(--accent-cyan)" }}
              >
                <Award size={13} /> Explain 지수 분해
              </button>
            )}
            <button className="btn-icon" onClick={onClose} title="닫기">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          {/* Relationships Grid */}
          <div style={{ marginBottom: "20px" }}>
            <div className="section-title">
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Link2 size={13} color="var(--accent-cyan)" />
                <span>활성 기술 패권 역학 관계 ({outgoing.length + incoming.length})</span>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
              {/* Outgoing */}
              <div style={{ background: "rgba(15, 23, 42, 0.6)", border: "1px solid var(--border-subtle)", borderRadius: "6px", padding: "12px" }}>
                <div style={{ fontSize: "10px", fontWeight: 700, color: "var(--text-muted)", marginBottom: "8px" }}>
                  발신 관계 (FROM: {entity.nameKo}, {outgoing.length})
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: "180px", overflowY: "auto" }}>
                  {outgoing.map((rel) => {
                    const to = entityMap.get(rel.toId);
                    const relMeta = REL_TYPE_LABELS[rel.relType] || { labelKo: rel.relType, color: "#06b6d4" };
                    return (
                      <div
                        key={rel.id}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          fontSize: "11px",
                          padding: "4px 6px",
                          background: "rgba(255,255,255,0.02)",
                          borderRadius: "4px",
                        }}
                      >
                        <span>➔ <strong>{to?.nameKo || rel.toId}</strong></span>
                        <span style={{ color: relMeta.color, fontWeight: 600, fontSize: "10px" }}>
                          {relMeta.labelKo} (강도 {rel.strength})
                        </span>
                      </div>
                    );
                  })}
                  {outgoing.length === 0 && (
                    <span style={{ fontSize: "11px", color: "var(--text-disabled)" }}>발신 관계 없음</span>
                  )}
                </div>
              </div>

              {/* Incoming */}
              <div style={{ background: "rgba(15, 23, 42, 0.6)", border: "1px solid var(--border-subtle)", borderRadius: "6px", padding: "12px" }}>
                <div style={{ fontSize: "10px", fontWeight: 700, color: "var(--text-muted)", marginBottom: "8px" }}>
                  수신 관계 (TO: {entity.nameKo}, {incoming.length})
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: "180px", overflowY: "auto" }}>
                  {incoming.map((rel) => {
                    const from = entityMap.get(rel.fromId);
                    const relMeta = REL_TYPE_LABELS[rel.relType] || { labelKo: rel.relType, color: "#06b6d4" };
                    return (
                      <div
                        key={rel.id}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          fontSize: "11px",
                          padding: "4px 6px",
                          background: "rgba(255,255,255,0.02)",
                          borderRadius: "4px",
                        }}
                      >
                        <span>⬅ <strong>{from?.nameKo || rel.fromId}</strong></span>
                        <span style={{ color: relMeta.color, fontWeight: 600, fontSize: "10px" }}>
                          {relMeta.labelKo} (강도 {rel.strength})
                        </span>
                      </div>
                    );
                  })}
                  {incoming.length === 0 && (
                    <span style={{ fontSize: "11px", color: "var(--text-disabled)" }}>수신 관계 없음</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Related Events */}
          <div>
            <div className="section-title">
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Calendar size={13} color="var(--accent-indigo)" />
                <span>관련 사건 목록 ({relatedEvents.length})</span>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: "200px", overflowY: "auto" }}>
              {relatedEvents.map((ev) => (
                <div
                  key={ev.id}
                  style={{
                    background: "rgba(19, 29, 51, 0.4)",
                    border: "1px solid var(--border-subtle)",
                    borderRadius: "6px",
                    padding: "8px 10px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    onClose();
                    onSelectEvent(ev);
                  }}
                >
                  <div>
                    <div style={{ fontSize: "12px", fontWeight: 600, color: "#ffffff" }}>
                      {ev.titleKo}
                    </div>
                    <div className="font-mono" style={{ fontSize: "10px", color: "var(--text-muted)" }}>
                      {new Date(ev.occurredAt).toLocaleDateString()} · {ev.eventType}
                    </div>
                  </div>
                  <span className="btn-secondary" style={{ fontSize: "10px" }}>사건 상세</span>
                </div>
              ))}
              {relatedEvents.length === 0 && (
                <span style={{ fontSize: "11px", color: "var(--text-disabled)" }}>등록된 사건 없음</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
